import { useReducer, Reducer } from "react";
import { v4 as uuidv4 } from "uuid";
import { ILanguage } from "../common/interfaces";
import { assertUnreachable } from "../common/shared";
import { LanguageModel } from "../models/BatchMessage/BatchMessageLanguageDetailModel";
import {
	Message,
	LanguageMessageItem,
	LanguageMessageState,
} from "../common/batchNotificationInterface";
import { allLanguages } from "../languages";

//define actions we can preform
type Action =
	| {
			type: "SET_MESSAGE_DETAIL_AND_LANGUAGE";
			payload: { id: string; message: Message; languages: ILanguage[] };
	  }
	| { type: "SET_LANGUAGES"; payload: { languages: ILanguage[] } }
	| { type: "ADD_NEW_MESSAGE_ITEM" }
	| { type: "REMOVE_MESSAGE_ITEM"; payload: { id: string } }
	| { type: "SET_MESSAGE_ITEM_TITLE"; payload: { id: string; title: string } }
	| {
			type: "SET_MESSAGE_ITEM_DETAIL";
			payload: {
				id: string;
				messageDetail: Message;
			};
	  }
	| {
			type: "INITIALIZE_BATCH_MESSAGE";
			payload: { languageList: LanguageModel[] };
	  };

/**
 * custom hook wrapping useReducer. note: this reducer is local to the component it is used in.
 * nice way of encapsulating logic of storing a schedulers data and possible actions you can do on that data away from the UI component.
 * this allows us to test this logic separately and reuse this logic across multiple components, for example between the test run and main scheduler.
 */
export const useBatchMessageConfiguration = () => {
	return useReducer<Reducer<LanguageMessageState, Action>, undefined>(
		(state, action): LanguageMessageState => {
			//reducer needs to return new state based on previous state and action parsed in
			switch (action.type) {
				case "SET_MESSAGE_DETAIL_AND_LANGUAGE":
					const newMessage = action.payload.message;
					const changedItems: LanguageMessageItem[] = [];
					for (const item of state.items) {
						if (item.rowId === action.payload.id) {
							item.messages = [newMessage];
						}
						changedItems.push(item);
					}

					return {
						languages: action.payload.languages,
						items: [...changedItems],
					};
				case "SET_LANGUAGES":
					const newLanguages: ILanguage[] = [];
					const codesAdded: string[] = [];
					const codesRemoved: string[] = [];

					state.languages.forEach((existingLanguage) => {
						const match = action.payload.languages.find(
							(e) => e.code === existingLanguage.code
						);
						if (!match) {
							codesRemoved.push(existingLanguage.code);
						}
					});
					action.payload.languages.forEach((newLanguage) => {
						const match = state.languages.find(
							(e) => e.code === newLanguage.code
						);
						if (!match) {
							codesAdded.push(newLanguage.code);
						}
						newLanguages.push(newLanguage);
					});

					let newItems = [...state.items];
					if (newItems.length === 0 && action.payload.languages.length > 0) {
						// language is selected and no message detail is added, add new row in table
						const messages: Message[] = [];
						messages.push({
							id: "",
							languageCode: action.payload.languages[0].code,
							fileName: "",
							message: "",
							audioDuration: 0,
						});
						newItems.push({
							rowId: uuidv4(),
							title: "",
							messages: messages,
						});
					} else if (
						newItems.length > 0 &&
						action.payload.languages.length === 0
					) {
						// all languages are de-selected and message detail is added, remove all rows
						newItems = [];
					} else {
						newItems.forEach((item) => {
							item.messages = item.messages.filter(
								(message) =>
									codesRemoved.findIndex((c) => c === message.languageCode) ===
									-1
							);
							codesAdded.forEach((code) => {
								const existingIndex = item.messages.findIndex(
									(message) => message.languageCode === code
								);
								if (existingIndex === -1) {
									item.messages.push({
										id: "",
										languageCode: code,
										fileName: "",
										message: "",
										audioDuration: 0,
									});
								}
							});
						});
					}

					return {
						items: newItems,
						languages: newLanguages,
					};
				case "ADD_NEW_MESSAGE_ITEM":
					const messages: Message[] = [];
					if (!state.languages.length) {
						// empty for initial WA message
						messages.push({
							id: "",
							languageCode: "",
							fileName: "",
							message: "",
							audioDuration: 0,
						});
					}
					state.languages.forEach((language: ILanguage) => {
						messages.push({
							id: "",
							languageCode: language.code,
							fileName: "",
							message: "",
							audioDuration: 0,
						});
					});
					return {
						...state,
						items: [
							...state.items,
							{
								rowId: uuidv4(),
								title: "",
								messages: messages,
							},
						],
					};
				case "REMOVE_MESSAGE_ITEM":
					const updatedItems = [...state.items];
					const itemIndexToRemove = updatedItems.findIndex(
						(e) => e.rowId === action.payload.id
					);
					if (itemIndexToRemove === -1) {
						return state;
					}
					updatedItems.splice(itemIndexToRemove, 1);
					return {
						...state,
						items: updatedItems,
					};
				case "SET_MESSAGE_ITEM_TITLE":
					const updatedItemsT = [...state.items];
					const itemIndexToUpdateTitle = updatedItemsT.findIndex(
						(e) => e.rowId === action.payload.id
					);
					if (itemIndexToUpdateTitle === -1) {
						return state;
					}
					updatedItemsT.splice(itemIndexToUpdateTitle, 1, {
						...updatedItemsT[itemIndexToUpdateTitle],
						title: action.payload.title,
					});
					return {
						...state,
						items: updatedItemsT,
					};
				case "SET_MESSAGE_ITEM_DETAIL":
					const updatedItemsM = [...state.items];
					const itemIndexToUpdateMessage = updatedItemsM.findIndex(
						(e) => e.rowId === action.payload.id
					);
					if (itemIndexToUpdateMessage === -1) {
						return state;
					}
					const newMessages = [
						...updatedItemsM[itemIndexToUpdateMessage].messages,
					];
					const existingMessageIndex = newMessages.findIndex(
						(m) => m.languageCode === action.payload.messageDetail.languageCode
					);
					if (existingMessageIndex === -1) {
						newMessages.push(action.payload.messageDetail);
					} else {
						newMessages.splice(
							existingMessageIndex,
							1,
							action.payload.messageDetail
						);
					}

					updatedItemsM.splice(itemIndexToUpdateMessage, 1, {
						...updatedItemsM[itemIndexToUpdateMessage],
						messages: newMessages,
					});
					return {
						...state,
						items: updatedItemsM,
					};
				case "INITIALIZE_BATCH_MESSAGE":
					const items: LanguageMessageItem[] = [];
					const languages: ILanguage[] = [];
					const messageTitleList: string[] = action.payload.languageList
						.filter((x) => x.code === action.payload.languageList[0].code)
						.map((m) => m.messageTitle);
					messageTitleList.forEach((message, index: any) => {
						items.push({
							rowId: uuidv4(),
							title: message,
							messages: [],
						});
					});
					action.payload.languageList.forEach((languageDetail) => {
						const lang = allLanguages.find(
							(l) => l.code === languageDetail.code
						);
						if (lang && languages.indexOf(lang) === -1) {
							languages.push(lang);
						}
						const item = items.find(
							(i) => i.title === languageDetail.messageTitle
						);
						if (item) {
							item.messages.push({
								id: languageDetail.id,
								languageCode: languageDetail.code,
								message: languageDetail.message,
								audioDuration: languageDetail.audioDuration,
								fileName: null,
							});
						}
					});
					return {
						items: items,
						languages: languages,
					};
			}
			assertUnreachable(action);
		},
		undefined,
		() => ({ items: [], languages: [] })
	);
};
